import { z } from 'zod';
import { RealtimeAgent, RealtimeSession, tool } from '@openai/agents/realtime';
import { openai } from '@ai-sdk/openai';
import { generateText } from 'ai';
import { GIVER_PROMPT } from '../../resources/prompts/giverPrompt';
import { RECIPIENT_PROMPT } from '../../resources/prompts/recipientPrompt';
import { OCCASION_PROMPT } from '../../resources/prompts/occasionPrompt';
import type { 
  VoiceFlowData, 
  VoiceFlowPhase,
  SessionConfig
} from '../types/orchestration';

// Utility function for prompt interpolation
function interpolatePrompt(prompt: string, vars: Record<string, string | undefined>): string {
  return prompt
    .replace(/{{userName}}/g, vars.userName || '')
    .replace(/{{recipientName}}/g, vars.recipientName || '');
}

// Helper function to get token
async function getVoiceToken(): Promise<string> {
  const response = await fetch(
    import.meta.env.VITE_TOKEN_ENDPOINT || '/api/voice-token'
  ).then(resp => resp.json());
  
  // Handle both Cloudflare worker format and Vercel format
  return response.client_secret || response.token;
}

// Zod schemas for tool parameters
const GiverDataSchema = z.object({
  name: z.string(),
  pronouns: z.string().nullable().optional(),
  cultural_tags: z.array(z.string()).default([]),
  giver_confidence: z.enum(['stressed', 'neutral', 'excited']),
  attachment_indicators: z.enum(['secure', 'anxious', 'avoidant', 'mixed']).nullable().optional(),
  giver_motive: z.enum(['norm', 'altruistic', 'self', 'expressive']),
  planning_style: z.enum(['planner', 'spontaneous', 'mix']),
  budget_range: z.string(),
  budget_flex: z.enum(['firm', 'flexible']),
  desired_emotion: z.string(),
  bias_awareness: z.enum(['moment_focused', 'long_term_value', 'balanced']).nullable().optional(),
  request_philosophy: z.enum(['fulfill_wishes', 'creative_surprise', 'balanced']).nullable().optional(),
  openness_pos: z.number().min(0).max(100),
  extraversion_pos: z.number().min(0).max(100),
  conversation_notes: z.string(),
  bias_flags: z.array(z.string()).optional().default([])
});

const RecipientDataSchema = z.object({
  // NEW DISCOVERY FIELDS
  current_joy: z.string().optional().default(""),
  problem_to_solve: z.string().optional().default(""),
  life_project: z.string().optional().default(""),
  free_text_persona: z.string().optional().default(""),
  treat_for_self: z.string().optional().default(""),
  
  // EXISTING FIELDS
  recipient_name: z.string().nullable().optional(),
  relationship_type: z.string(),
  relationship_duration: z.string(),
  engagement_level: z.enum(['quick', 'full']),
  recipient_big5: z.object({
    openness: z.number().min(0).max(1),
    extraversion: z.number().min(0).max(1),
    agreeableness: z.number().min(0).max(1).nullable().optional(),
    conscientiousness: z.number().min(0).max(1).nullable().optional(),
    neuroticism: z.number().min(0).max(1).nullable().optional()
  }),
  attachment_cue: z.enum(['secure', 'anxious', 'avoidant', 'unsure']),
  surprise_tolerance: z.enum(['low', 'medium', 'high']),
  core_values: z.array(z.enum(['tradition', 'achievement', 'creativity', 'helping_others', 'adventure', 'security', 'independence', 'fun'])).min(1).max(3),
  love_language: z.enum(['quality_time', 'words_of_affirmation', 'thoughtful_gifts', 'acts_of_service', 'physical_affection']),
  aesthetic_sensitivity: z.enum(['low', 'medium', 'high']),
  minimalist_tendency: z.enum(['accumulator', 'balanced', 'minimalist']),
  wellness_priority: z.enum(['low', 'medium', 'high']),
  expertise_depth: z.enum(['casual', 'focused', 'obsessive']),
  environmental_consciousness: z.enum(['low', 'medium', 'high']),
  analog_preference: z.enum(['digital_native', 'balanced', 'analog_lover']),
  social_orientation: z.enum(['individual_focused', 'balanced', 'group_focused']),
  innovation_adoption: z.enum(['early_adopter', 'moderate', 'traditional']).nullable().optional(),
  sentimentality_level: z.enum(['low', 'medium', 'high']).nullable().optional(),
  brand_consciousness: z.enum(['low', 'medium', 'high']).nullable().optional(),
  humor_appreciation: z.enum(['subtle', 'balanced', 'playful']).nullable().optional(),
  restrictions: z.array(z.string()).default([]),
  preferences: z.array(z.string()).default([]),
  gift_stories: z.string().nullable().optional(),
  budget_range: z.string(),
  budget_confidence: z.enum(['firm', 'flexible']),
  cultural_context: z.object({
    gift_giving_norms: z.enum(['individual', 'family', 'collective']).nullable().optional(),
    formality_preference: z.enum(['casual', 'traditional', 'formal']).nullable().optional()
  }).nullable().optional()
});

const ContextDataSchema = z.object({
  occasion_type: z.string(),
  relationship_type: z.string(),
  relationship_power: z.enum(['equal', 'giver_higher', 'recipient_higher', 'complex']),
  relationship_security: z.enum(['secure', 'cautious', 'complex']),
  occasion_significance: z.enum(['1', '2', '3', '4', '5']),
  reciprocity_norm: z.enum(['mutual', 'one_way', 'complex', 'unsure']),
  comm_style: z.enum(['playful', 'heartfelt', 'direct', 'formal']),
  inside_joke_flag: z.boolean(),
  joke_text: z.string().nullable().optional(),
  symbol_tag: z.enum(['receiver_symbolic', 'togetherness', 'conventional', 'milestone']),
  budget_range: z.string(),
  timing: z.string(),
  tone_keyword: z.string(),
  bias_flags: z.array(z.string()).optional().default([]),
  special_considerations: z.string().nullable().optional(),
  cultural_tags: z.array(z.string()).optional().default([])
});

type EventCallback = (data?: Record<string, unknown>) => void;

export class GVVRVoiceOrchestrator {
  private flowData: VoiceFlowData = {};
  private currentPhase: VoiceFlowPhase = 'ready';
  private session: RealtimeSession | null = null;
  private eventListeners: Record<string, EventCallback[]> = {};
  private recipientName: string | null = null;

  // AI SDK model for secure parallel analysis
  private aiModel: ReturnType<typeof openai> | null = null;
  private aiModelReady = false;

  // Tool definitions (each agent gets specific tools)
  private giverTool = tool({
    name: 'complete_giver_data_collection',
    description: 'Captures comprehensive giver psychology data after conversation',
    parameters: GiverDataSchema,
    execute: async (data: z.infer<typeof GiverDataSchema>) => {
      console.log('🎯 Giver data collected, starting parallel analysis...');
      this.flowData.giver = data;

      // Start real-time insights during analysis
      this.showVisualInsight('🧠 Analyzing your gift-giving psychology...');
      
      // Run parallel analysis for deeper insights
      const giverAnalysis = await this.analyzeGiverArchetypeParallel(data);
      
      // Store comprehensive analysis
      this.flowData.giverAnalysis = {
        primaryArchetype: giverAnalysis.primaryArchetype as string,
        confidence: giverAnalysis.confidence as number,
        reasoning: giverAnalysis.reasoning as string,
        personalizedInsights: giverAnalysis.personalizedInsights as string[],
        qualityScore: Math.min((giverAnalysis.confidence as number) + 5, 100),
        missingFields: [],
        status: 'COMPLETE' as const
      };

      // Generate some insights from conversation during analysis  
      if (data.conversation_notes) {
        await this.generateRealtimeInsights(data.conversation_notes);
      }
      
      this.currentPhase = 'recipient';
      this.emit('phase_transition', { from: 'giver', to: 'recipient', data });
      
      // Create personalized response based on actual archetype
      const insights = this.flowData.giverAnalysis?.personalizedInsights?.[0] || 'You have a thoughtful approach to gift-giving';
      
      return `Amazing! You're a ${this.flowData.giverAnalysis?.primaryArchetype || 'Thoughtful Curator'}! ${insights}. Now let's understand who you're shopping for...`;
    }
  });

  private recipientTool = tool({
    name: 'complete_recipient_data_collection', 
    description: 'Captures comprehensive recipient psychology data and relationship dynamics',
    parameters: RecipientDataSchema,
    execute: async (data: z.infer<typeof RecipientDataSchema>) => {
      console.log('🎯 RECIPIENT TOOL CALLED - Starting parallel analysis...');
      console.log('🔄 Current phase before transition:', this.currentPhase);
      console.log('📊 Recipient data received:', data);
      
      // Check if we have enough data to proceed
      const hasMinimumData = data && Object.keys(data).length > 0;
      
      if (!hasMinimumData) {
        console.warn('⚠️ Insufficient recipient data, using basic fallback analysis');
        // Don't transition yet - let Grace continue gathering data
        return "I need a bit more information about the recipient. Can you tell me more about their personality and interests?";
      }
      
      // Store recipient name from flow initialization or use default
      if (!data.recipient_name) {
        data.recipient_name = this.recipientName || 'Recipient';
      }
      
      this.flowData.recipient = data;
      
      // Show real-time insights during analysis
      this.showVisualInsight('🎯 Analyzing recipient personality patterns...');
      
      // Generate archetype-specific insights during analysis
      this.showArchetypeInsights(data);
      
      // Run parallel recipient analysis
      const recipientAnalysis = await this.analyzeRecipientArchetypeParallel(data);
      
      this.flowData.recipientAnalysis = {
        primaryArchetype: recipientAnalysis.primaryArchetype as string,
        confidence: recipientAnalysis.confidence as number,
        reasoning: recipientAnalysis.reasoning as string,
        personalizedInsights: recipientAnalysis.personalizedInsights as string[],
        qualityScore: Math.min((recipientAnalysis.confidence as number) + 3, 100),
        missingFields: [],
        status: 'COMPLETE' as const
      };
      
      console.log('🧠 Recipient analysis complete:', this.flowData.recipientAnalysis);
      
      this.currentPhase = 'context';
      console.log('📈 Phase updated to:', this.currentPhase);
      
      this.emit('phase_transition', { from: 'recipient', to: 'context', data });
      console.log('🚀 Phase transition event emitted: recipient → context');
      
      const giverName = this.flowData.giver?.name || 'there';
      const giverArchetype = this.flowData.giverAnalysis?.primaryArchetype || 'thoughtful giver';
      const recipientArchetype = this.flowData.recipientAnalysis?.primaryArchetype || 'interesting recipient';
      
      // Show relationship dynamic insight
      this.showVisualInsight(`✨ ${giverArchetype} + ${recipientArchetype} = exciting gift potential!`);
      
      const response = `Perfect! I understand you're a ${giverArchetype} shopping for a ${recipientArchetype}, ${giverName}. This is a fascinating combination! Now let's talk about the occasion and context.`;
      
      console.log('💬 Recipient tool response:', response);
      return response;
    }
  });

  private contextTool = tool({
    name: 'complete_context_data_collection',
    description: 'Complete the context profiling and generate final recommendations',
    parameters: ContextDataSchema,
    execute: async (data: z.infer<typeof ContextDataSchema>) => {
      console.log('🎯 CONTEXT TOOL CALLED - Data received:', data);
      console.log('🔄 Current phase before transition:', this.currentPhase);
      console.log('📊 Context data validation:');
      console.log('  - occasion_type:', data.occasion_type ? '✅' : '❌', data.occasion_type);
      console.log('  - budget_range:', data.budget_range ? '✅' : '❌', data.budget_range);
      console.log('  - timing:', data.timing ? '✅' : '❌', data.timing);
      
      this.flowData.context = data;
      
      this.currentPhase = 'recommendations';
      this.emit('phase_transition', { from: 'context', to: 'recommendations', data });
      
      // Generate recommendations
      const recommendations = await this.generateFinalRecommendations();
      
      this.currentPhase = 'complete';
      this.emit('flow_completed', { recommendations });
      
      // Close the voice session to end microphone access
      console.log('🎙️ Closing voice session - microphone access ended');
      await this.closeSession();
      
      return `Perfect! I've generated your personalized gift recommendations. Thank you for the wonderful conversation!`;
    }
  });

  constructor() {
    this.initializeAIModel();
    console.log('🔧 GVVR Voice Orchestrator initialized with handoff pattern');
  }

  private async initializeAIModel(): Promise<void> {
    if (this.aiModelReady) return;
    
    try {
      // Get API key securely from our token system (same pattern as voice token)
      await fetch('/api/voice-token');
      
      // Note: For now, we'll use our existing API endpoints instead of direct AI SDK
      // TODO: Create a dedicated AI SDK token endpoint when needed
      this.aiModelReady = true;
    } catch (error) {
      console.warn('⚠️ Could not initialize AI SDK model:', error);
      this.aiModelReady = false;
    }
  }

  // Event system
  on(event: string, callback: EventCallback) {
    if (!this.eventListeners[event]) {
      this.eventListeners[event] = [];
    }
    this.eventListeners[event].push(callback);
  }

  off(event: string, callback: EventCallback) {
    if (!this.eventListeners[event]) return;
    const index = this.eventListeners[event].indexOf(callback);
    if (index > -1) {
      this.eventListeners[event].splice(index, 1);
    }
  }

  private emit(event: string, data?: Record<string, unknown>): void {
    console.log(`🎯 GVVR Event: ${event}`, data);
    
    // Emit custom DOM events for UI updates
    document.dispatchEvent(new CustomEvent(`gvvr:${event}`, { detail: data }));
    
    // Analytics tracking
    if (event.includes('phase_') || event.includes('flow_')) {
      this.trackAnalytics(event, data);
    }
  }

  private trackAnalytics(event: string, data?: Record<string, unknown>): void {
    // Analytics implementation
    console.log('📊 Analytics:', { event, data, timestamp: new Date().toISOString() });
  }

  // Visual Insight Display Method
  private showVisualInsight(insight: string): void {
    this.emit('analysis_insight', { 
      phase: this.currentPhase,
      insight: insight,
      timestamp: Date.now()
    });
    console.log(`💡 ${insight}`);
  }

  // Secure Token-Based Parallel Analysis
  private async analyzeGiverArchetypeParallel(data: z.infer<typeof GiverDataSchema>): Promise<Record<string, unknown>> {
    console.log('🧠 Starting secure parallel giver analysis...');
    
    try {
      // Use existing secure API infrastructure
      return await this.fallbackToAPIAnalysis(data as Record<string, unknown>, 'giver');
    } catch (error) {
      console.error('❌ Parallel analysis failed:', error);
      return this.generateBasicAnalysis(data as Record<string, unknown>, 'giver');
    }
  }

  private async analyzeRecipientArchetypeParallel(data: z.infer<typeof RecipientDataSchema>): Promise<Record<string, unknown>> {
    console.log('🎯 Starting secure parallel recipient analysis...');
    
    try {
      // Use existing secure API infrastructure for now
      return await this.fallbackToAPIAnalysis(data as Record<string, unknown>, 'recipient');
    } catch (parallelError) {
      console.error('❌ Parallel recipient analysis failed:', parallelError);
      return this.generateBasicAnalysis(data as Record<string, unknown>, 'recipient');
    }
  }

  private async generateRealtimeInsights(conversationChunk: string): Promise<void> {
    if (!conversationChunk || conversationChunk.length < 20) return;

    await this.initializeAIModel();
    
    if (this.aiModelReady && this.aiModel) {
      try {
        const insightPrompt = `Generate 1-2 brief real-time insights about personality patterns in this conversation chunk:
        
"${conversationChunk}"

Format as short, engaging observations with emojis. Examples:
"🎯 Strong planning language detected!"
"💝 Care-focused motivation emerging"
"⚡ High energy around gift experiences"

Return only the insights, no extra text.`;

        const { text } = await generateText({
          model: this.aiModel,
          prompt: insightPrompt,
          maxTokens: 150,
          temperature: 0.8
        });
        
        const insights = text.split('\n').filter((line: string) => line.trim());
        insights.forEach((insight: string, index: number) => {
          setTimeout(() => this.showVisualInsight(insight.trim()), index * 800);
        });
        return;
      } catch (error) {
        console.log('⚡ AI SDK insights failed, using fallback:', (error as Error).message);
      }
    }

    // Fallback to simple insights
    const insights = [
      '🧠 Analyzing conversation patterns...',
      '💝 Extracting personality signals...',
      '⚡ Building psychological profile...'
    ];
    
    insights.forEach((insight: string, index: number) => {
      setTimeout(() => this.showVisualInsight(insight), index * 800);
    });
  }

  private async fallbackToAPIAnalysis(data: Record<string, unknown>, type: 'giver' | 'recipient'): Promise<Record<string, unknown>> {
    console.log(`📡 Calling ${type} analysis API as fallback...`);
    
    try {
      const endpoint = type === 'giver' ? '/api/analyze-archetype' : '/api/analyze-recipient';
      
      // Different payload formats for different endpoints
      let requestBody;
      if (type === 'recipient') {
        // Convert our structured recipient data to the question format the API expects
        const recipientData = data as z.infer<typeof RecipientDataSchema>;
        const mappedAnswers = this.mapRecipientDataToAPIFormat(recipientData);
        const profileType = recipientData.engagement_level === 'quick' ? 'quick' : 'full';
        
        requestBody = {
          answers: mappedAnswers,
          profileType: profileType
        };
      } else {
        // Giver API expects { answers, culturalContext }
        requestBody = {
          answers: data,
          culturalContext: (data as Record<string, unknown>).cultural_tags || null
        };
      }
      
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`API call failed: ${response.status} - ${errorText}`);
      }
      return await response.json() as Record<string, unknown>;
    } catch (apiError) {
      console.error(`❌ ${type} API fallback failed:`, apiError);
      return this.generateBasicAnalysis(data, type);
    }
  }

  // Map our enhanced recipient data to the question format the API expects
  private mapRecipientDataToAPIFormat(data: z.infer<typeof RecipientDataSchema>): Record<string, unknown> {
    const mappedAnswers: Record<string, unknown> = {};
    
    // Map core relationship data (Q201, Q202)
    mappedAnswers['201'] = data.relationship_type;
    mappedAnswers['202'] = data.relationship_duration;
    
    // Map personality signals (Q214) - primary archetype indicators
    const personalitySignals: string[] = [];
    
    // Map love language to personality signals
    const loveLanguageMap = {
      'quality_time': 'Creative soul',
      'words_of_affirmation': 'Memory keeper',
      'thoughtful_gifts': 'Quality perfectionist',
      'acts_of_service': 'Practical helper',
      'physical_affection': 'Social connector'
    };
    if (data.love_language && loveLanguageMap[data.love_language]) {
      personalitySignals.push(loveLanguageMap[data.love_language]);
    }
    
    // Map core values to personality signals
    if (data.core_values?.includes('creativity')) personalitySignals.push('Creative soul');
    if (data.core_values?.includes('helping_others')) personalitySignals.push('Social connector');
    if (data.core_values?.includes('achievement')) personalitySignals.push('Status aware');
    if (data.core_values?.includes('adventure')) personalitySignals.push('Adventure seeker');
    if (data.core_values?.includes('tradition')) personalitySignals.push('Memory keeper');
    
    // Map lifestyle dimensions to personality signals
    if (data.aesthetic_sensitivity === 'high') personalitySignals.push('Quality perfectionist');
    if (data.minimalist_tendency === 'minimalist') personalitySignals.push('Minimalist thinker');
    if (data.wellness_priority === 'high') personalitySignals.push('Wellness focused');
    if (data.expertise_depth === 'obsessive') personalitySignals.push('Collection builder');
    if (data.environmental_consciousness === 'high') personalitySignals.push('Eco-conscious');
    if (data.innovation_adoption === 'early_adopter') personalitySignals.push('Tech enthusiast');
    
    mappedAnswers['214'] = personalitySignals.slice(0, 3); // Max 3 signals
    
    // Map engagement level (Q215)
    const engagementMap = {
      'casual': 'Casual',
      'focused': 'Focused', 
      'obsessive': 'Serious'
    };
    mappedAnswers['215'] = engagementMap[data.expertise_depth] || 'Focused';
    
    // Map restrictions and preferences (Q216)
    const restrictions: string[] = [];
    const otherRestrictions: string[] = [];
    
    // Map our restriction arrays to the expected format
    if (data.restrictions && data.restrictions.length > 0) {
      data.restrictions.forEach(restriction => {
        restrictions.push(restriction);
      });
    }
    
    // Add lifestyle preferences as restrictions
    if (data.minimalist_tendency === 'minimalist') {
      restrictions.push('No clutter/minimalist');
    }
    if (data.environmental_consciousness === 'high') {
      restrictions.push('Eco-conscious only');
    }
    
    // Add enhanced signals as restrictions if available
    if (data.brand_consciousness === 'high') {
      restrictions.push('Brand conscious');
    }
    if (data.sentimentality_level === 'high') {
      restrictions.push('Values sentimental items');
    }
    
    // Add budget info
    if (data.budget_range) {
      otherRestrictions.push(`Budget: ${data.budget_range}`);
    }
    
    // Add social orientation context
    if (data.social_orientation === 'group_focused') {
      otherRestrictions.push('Prefers group experiences');
    } else if (data.social_orientation === 'individual_focused') {
      otherRestrictions.push('Prefers individual experiences');
    }
    
    mappedAnswers['216'] = {
      selections: restrictions,
      otherRestrictions: otherRestrictions.join('; ') || null
    };
    
    // Map gift stories and preferences (Q217)
    const giftStories: string[] = [];
    if (data.gift_stories) giftStories.push(data.gift_stories);
    if (data.preferences && data.preferences.length > 0) {
      giftStories.push(`Interests: ${data.preferences.join(', ')}`);
    }
    
    // Add new discovery fields to gift stories
    if (data.current_joy) giftStories.push(`Current joy: ${data.current_joy}`);
    if (data.problem_to_solve) giftStories.push(`Problem to solve: ${data.problem_to_solve}`);
    if (data.treat_for_self) giftStories.push(`Self-treat preference: ${data.treat_for_self}`);
    if (data.free_text_persona) giftStories.push(`Persona description: ${data.free_text_persona}`);
    
    mappedAnswers['217'] = giftStories.join(' | ') || 'No specific gift history provided';
    
    // Add traditional personality mapping for better analysis
    if (data.recipient_big5?.openness !== undefined) {
      mappedAnswers['204'] = data.recipient_big5.openness > 0.5 ? 'New adventures' : 'Cozy familiar spaces';
    }
    
    if (data.surprise_tolerance) {
      const surpriseMap = {
        'high': 'Love surprises',
        'medium': 'Thoughtful surprises',
        'low': 'Prefer knowing'
      };
      mappedAnswers['208'] = surpriseMap[data.surprise_tolerance];
    }
    
    console.log('🔄 Mapped enhanced recipient data to API format:', mappedAnswers);
    return mappedAnswers;
  }

  private generateBasicAnalysis(_data: Record<string, unknown>, type: 'giver' | 'recipient'): Record<string, unknown> {
    // Basic fallback analysis
    return {
      primaryArchetype: type === 'giver' ? 'Thoughtful Curator' : 'Useful Pragmatist',
      confidence: 60,
      reasoning: 'Analysis based on basic conversation patterns due to processing limitations',
      personalizedInsights: ['Values thoughtful gift-giving', 'Considers recipient preferences'],
      source: 'basic_fallback'
    };
  }

  private showArchetypeInsights(data: z.infer<typeof RecipientDataSchema>): void {
    // Generate real-time archetype insights based on collected data
    const insights: string[] = [];
    
    // Love language insights
    if (data.love_language) {
      const loveLanguageMap = {
        'quality_time': '⏰ Quality time focus suggests Experiential Creative tendencies',
        'words_of_affirmation': '💬 Words of affirmation hints at Sentimental Storykeeper traits',
        'thoughtful_gifts': '🎁 Gift love language indicates Aesthetic Curator potential',
        'acts_of_service': '🔧 Service preference suggests Useful Pragmatist leanings',
        'physical_affection': '🤗 Physical affection preference shows Romantic Idealist traits'
      };
      insights.push(loveLanguageMap[data.love_language]);
    }
    
    // Values-based insights
    if (data.core_values?.includes('creativity') && data.aesthetic_sensitivity === 'high') {
      insights.push('🎨 High creativity + aesthetic sensitivity = Aesthetic Curator signals');
    }
    if (data.core_values?.includes('helping_others') && data.social_orientation === 'group_focused') {
      insights.push('🤝 Helper values + group focus = Social Connector patterns');
    }
    if (data.core_values?.includes('achievement') && data.brand_consciousness === 'high') {
      insights.push('🏆 Achievement values + brand consciousness = Status Seeker indicators');
    }
    
    // Lifestyle insights
    if (data.minimalist_tendency === 'minimalist' && data.environmental_consciousness === 'high') {
      insights.push('🌱 Minimalist + eco-conscious = Intentional Minimalist archetype');
    }
    if (data.expertise_depth === 'obsessive' && data.aesthetic_sensitivity === 'high') {
      insights.push('🔍 Deep expertise + aesthetic sense = Collector Specialist traits');
    }
    
    // Show insights with delays for natural flow
    insights.forEach((insight, index) => {
      setTimeout(() => this.showVisualInsight(insight), (index + 1) * 1200);
    });
  }

  private async generateFinalRecommendations() {
    console.log('🎯 Generating Final Recommendations...');
    
    try {
      // Transform flowData into the format expected by the API
      const giverProfile = {
        name: this.flowData.giver?.name || 'User',
        archetype: this.flowData.giverAnalysis?.primaryArchetype || 'Thoughtful Curator',
        confidence: this.flowData.giverAnalysis?.confidence || 60,
        traits: [], // Could be extracted from analysis if available
        insights: this.flowData.giverAnalysis?.personalizedInsights || [],
        motive: this.flowData.giver?.giver_motive || 'altruistic',
        culturalContext: this.flowData.giver?.cultural_tags?.join(', ') || null
      };

      const recipientProfile = {
        name: this.flowData.recipient?.recipient_name || 'Recipient',
        archetype: this.flowData.recipientAnalysis?.primaryArchetype || 'Useful Pragmatist',
        confidence: this.flowData.recipientAnalysis?.confidence || 60,
        traits: [], // Could be extracted from recipient analysis
        relationship: {
          type: this.flowData.recipient?.relationship_type || 'friend',
          duration: this.flowData.recipient?.relationship_duration || 'unknown'
        },
        interests: this.flowData.recipient?.preferences?.join(', ') || 'Not specified',
        budget: this.flowData.recipient?.budget_range || this.flowData.context?.budget_range || 'Not specified',
        // New enhanced fields
        currentJoy: this.flowData.recipient?.current_joy || 'Not specified',
        problemToSolve: this.flowData.recipient?.problem_to_solve || 'None identified',
        treatForSelf: this.flowData.recipient?.treat_for_self || 'Not specified'
      };

      const occasionContext = {
        type: this.flowData.context?.occasion_type || 'general',
        budget: this.flowData.context?.budget_range || 'Not specified',
        timeline: this.flowData.context?.timing || 'flexible',
        emotionGoals: [this.flowData.context?.tone_keyword || 'happy'],
        symbolTag: this.flowData.context?.symbol_tag || 'conventional',
        communicationStyle: this.flowData.context?.comm_style || 'heartfelt',
        powerDynamic: this.flowData.context?.relationship_power || 'equal',
        reciprocityNorm: this.flowData.context?.reciprocity_norm || 'mutual',
        specialContext: this.flowData.context?.special_considerations || null
      };

      console.log('📡 Calling recommendations API with profiles:', {
        giverProfile,
        recipientProfile,
        occasionContext
      });

      const response = await fetch('/api/generate-recommendations', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          giverProfile,
          recipientProfile,
          occasionContext
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Recommendations API failed: ${response.status} - ${errorText}`);
      }

      const recommendations = await response.json();
      console.log('✅ Generated personalized recommendations:', recommendations);
      
      return recommendations;

    } catch (error) {
      console.error('❌ Failed to generate recommendations, using fallback:', error);
      
      // Enhanced fallback recommendations
      return {
        recommendations: [
          {
            title: 'Current Joy Enhancement',
            description: `Something that builds on their current interest: ${this.flowData.recipient?.current_joy || 'their recent passions'}.`,
            reasoning: 'Leverages what\'s already bringing them happiness for immediate positive impact.',
            psychologicalFit: 'Amplifies existing positive energy and shows attention to their current state.',
            category: 'Interest-Based',
            tags: ['current', 'joy', 'personalized'],
            budgetFit: this.flowData.context?.budget_range || 'moderate'
          },
          {
            title: 'Problem-Solving Gift',
            description: `A thoughtful solution for: ${this.flowData.recipient?.problem_to_solve || 'making their life easier'}.`,
            reasoning: 'Addresses a real need or challenge they\'ve mentioned.',
            psychologicalFit: 'Shows care and practical consideration for their wellbeing.',
            category: 'Practical',
            tags: ['helpful', 'caring', 'useful'],
            budgetFit: this.flowData.context?.budget_range || 'moderate'
          },
          {
            title: 'Personalized Experience',
            description: `A curated experience that matches their interests and creates lasting memories for this ${this.flowData.context?.occasion_type || 'special occasion'}.`,
            reasoning: `Based on the ${this.flowData.giverAnalysis?.primaryArchetype || 'Thoughtful Giver'} and ${this.flowData.recipientAnalysis?.primaryArchetype || 'Appreciative Recipient'} pairing, an experience would resonate well.`,
            psychologicalFit: 'This recommendation bridges the gap between the giver\'s approach and the recipient\'s preferences.',
            category: 'Experience',
            tags: ['memorable', 'personal', 'meaningful'],
            budgetFit: this.flowData.context?.budget_range || 'moderate'
          }
        ],
        psychologicalSummary: `The ${this.flowData.giverAnalysis?.primaryArchetype || 'Thoughtful Giver'} and ${this.flowData.recipientAnalysis?.primaryArchetype || 'Appreciative Recipient'} dynamic suggests thoughtful compatibility with meaningful gift exchanges.`,
        giverRecipientDynamic: `This pairing shows potential for meaningful gift exchanges that honor both personalities.`,
        occasionFit: `The ${this.flowData.context?.occasion_type || 'occasion'} context calls for something that creates positive lasting memories.`,
        fallbackNote: 'These recommendations use our backup analysis system to ensure you still get quality suggestions.'
      };
    }
  }

  // Create agents with handoffs
  private createAgents(userName: string, recipientName?: string) {
    const giverAgent = new RealtimeAgent({
      name: 'Grace - Giver Profiler',
      voice: 'sage',
      instructions: interpolatePrompt(GIVER_PROMPT, { userName, recipientName }),
      tools: [this.giverTool],
      handoffDescription: 'Profiles the gift giver and hands off to recipient profiling'
    });

    const recipientAgent = new RealtimeAgent({
      name: 'Grace - Recipient Profiler',
      voice: 'sage',
      instructions: interpolatePrompt(RECIPIENT_PROMPT, { userName, recipientName }),
      tools: [this.recipientTool],
      handoffDescription: 'Profiles the gift recipient and hands off to context profiling'
    });

    const contextAgent = new RealtimeAgent({
      name: 'Grace - Context Specialist',
      voice: 'sage',
      instructions: interpolatePrompt(OCCASION_PROMPT, { userName, recipientName }),
      tools: [this.contextTool],
      handoffDescription: 'Profiles the context and generates final recommendations'
    });

    // Set up handoffs in a cycle
    giverAgent.handoffs = [recipientAgent];
    recipientAgent.handoffs = [contextAgent];
    // contextAgent doesn't need handoffs - it completes the flow

    return { giverAgent, recipientAgent, contextAgent };
  }

  /**
   * Main orchestration method using handoffs
   */
  async executeGVVRFlow(userName: string, recipientName?: string, sessionConfig?: SessionConfig): Promise<unknown> {
    this.emit('flow_started', { userName, recipientName, sessionConfig });
    
    // Store recipient name for use in tools
    this.recipientName = recipientName || null;
    
    try {
      const { giverAgent } = this.createAgents(userName, recipientName);
      
      // Create single session that will handle all phases via handoffs
      this.session = new RealtimeSession(giverAgent);
      this.currentPhase = 'giver';

      // Set up event listeners for phase transitions (only log important events)
      this.session.on('transport_event', (event) => {
        // Only log meaningful events, not verbose deltas
        const importantEvents = [
          'session.created',
          'session.updated', 
          'error',
          'response.done',
          'conversation.item.created',
          'response.function_call_arguments.done',
          'input_audio_buffer.speech_started',
          'input_audio_buffer.speech_stopped'
        ];
        
        if (importantEvents.includes(event.type)) {
          console.log(`🎙️ Voice event: ${event.type}`, event);
        }

        // Handle session updates to configure voice settings
        if (event.type === 'session.updated') {
          this.handleSessionUpdated();
        }
      });

      // Enhanced error handling for voice session
      this.session.on('error', (error) => {
        console.error('❌ Voice session error:', error);
        
        // Better error message extraction for different error types
        let errorMessage = 'Voice session error';
        
        if (error && typeof error === 'object') {
          // Handle RTCErrorEvent specifically
          if (error.type === 'error' && error.error) {
            const rtcError = error.error;
            console.log('🔍 RTC Error Details:', rtcError);
            
            if (rtcError instanceof RTCError) {
              errorMessage = `WebRTC Error: ${rtcError.errorDetail} - ${rtcError.message}`;
            } else if (rtcError instanceof DOMException) {
              errorMessage = `WebRTC DOMException: ${rtcError.name} - ${rtcError.message}`;
            } else if (rtcError instanceof Error) {
              errorMessage = `WebRTC Error: ${rtcError.message}`;
            } else {
              errorMessage = `WebRTC Error: ${JSON.stringify(rtcError)}`;
            }
          }
          // Handle standard Error objects
          else if (error instanceof Error) {
            errorMessage = `Session Error: ${error.message}`;
          }
          // Handle objects with message property (check existence first)
          else if ('message' in error && typeof error.message === 'string') {
            errorMessage = `Session Error: ${error.message}`;
          }
          // Fallback for unknown object types
          else {
            errorMessage = `Unknown Error: ${JSON.stringify(error)}`;
          }
        } else {
          errorMessage = String(error) || 'Unknown voice session error';
        }
        
        console.error('🔍 Detailed error:', errorMessage);
        this.currentPhase = 'error';
        
        // Add user-friendly error message for UI display
        let userFriendlyMessage = errorMessage;
        if (errorMessage.includes('WebRTC') || errorMessage.includes('microphone')) {
          userFriendlyMessage = 'Voice connection failed. Please check your microphone permissions and try again.';
        } else if (errorMessage.includes('DOMException') && errorMessage.includes('NotAllowedError')) {
          userFriendlyMessage = 'Microphone access denied. Please allow microphone access and refresh the page.';
        } else if (errorMessage.includes('NotFoundError')) {
          userFriendlyMessage = 'No microphone found. Please connect a microphone and try again.';
        }
        
        this.emit('flow_error', { 
          error: errorMessage, 
          userMessage: userFriendlyMessage,
          timestamp: Date.now()
        });
      });

      // Get token and connect
      const token = await getVoiceToken();
      console.log('🔑 Connecting to voice session...');
      
      await this.session.connect({
        apiKey: token
      });
      console.log('✅ Voice session connected! Grace will guide you through all phases automatically.');
      
      // Return a promise that resolves when the flow is complete
      return new Promise((resolve, reject) => {
        const handleComplete = (event: CustomEvent) => {
          resolve(event.detail.recommendations);
        };
        
        const handleError = (error: unknown) => {
          reject(error);
        };

        // Listen for completion via our custom events
        document.addEventListener('gvvr:flow_completed', handleComplete as EventListener, { once: true });
        this.on('flow_error', handleError);
        
        // Cleanup listeners
        const cleanup = () => {
          document.removeEventListener('gvvr:flow_completed', handleComplete as EventListener);
          this.off('flow_error', handleError);
        };
        
        // Set up timeout (15 minutes for full conversation)
        setTimeout(() => {
          cleanup();
          reject(new Error('Voice flow timeout after 15 minutes'));
        }, 15 * 60 * 1000);
      });
      
    } catch (error) {
      this.currentPhase = 'error';
      this.emit('flow_error', { error: (error as Error).message });
      throw error;
    }
  }

  private handleSessionUpdated(): void {
    // Handle any session configuration updates if needed
    console.log('🔄 Session updated');
  }

  // Public interface methods
  getCurrentPhase(): VoiceFlowPhase {
    return this.currentPhase;
  }

  getFlowData(): VoiceFlowData {
    return this.flowData;
  }

  async emergencyStop(): Promise<void> {
    try {
      console.log('🚨 Emergency stop initiated');
      if (this.session) {
        await this.session.close();
        this.session = null;
      }
      this.currentPhase = 'ready';
      this.emit('emergency_stop', {});
      console.log('✅ Emergency stop completed');
    } catch (error) {
      console.error('❌ Error during emergency stop:', error);
    }
  }

  async closeSession(): Promise<void> {
    if (this.session) {
      console.log('🎙️ Gracefully closing voice session...');
      await this.session.close();
      this.session = null;
      console.log('✅ Voice session closed - microphone access ended');
    }
  }

  isReady(): boolean {
    return this.currentPhase === 'ready';
  }

  isActive(): boolean {
    return ['giver', 'recipient', 'context', 'recommendations'].includes(this.currentPhase);
  }

  isComplete(): boolean {
    return this.currentPhase === 'complete';
  }

  hasError(): boolean {
    return this.currentPhase === 'error';
  }
} 